import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { ParagraphOutputCarousel, CarouselCardItem } from "@/model";
import { useScenarioAutocompletes, useSyncForm } from "@/hook";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { getNewCarousel, isValidMediaData, removeMediaData } from "@/util/helper/paragraphHelper";
import Button from "@/component/Button";
import { TextInput } from "@/component/input";
import { SortableItem, SortableItemWrapper, SortableWrapper, toSortableItem } from "@/component/SortableWrapper";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";

import { ParagraphInputsProps } from "../model";
import { MediaInput, ButtonSortableList, MediaPreview } from "../input";


const ParagraphOutputCarouselCardInputs: React.FC<ParagraphInputsProps<ParagraphOutputCarousel>> = (props) => {
    const { paragraph, onChange } = props;

    const intl = useIntl();
    const [cards, setCards] = useState<SortableItem<CarouselCardItem>[]>((paragraph.data ?? [getNewCarousel()]).map(toSortableItem));

    const update = (cards: SortableItem<CarouselCardItem>[]) => {
        setCards(cards);
        onChange({
            ...paragraph,
            data: cards.map(item => item.data)
        });
    };

    const syncFromProps = useCallback(() => {
        if (!paragraph.data) {
            return;
        }
        setCards(paragraph.data.map(toSortableItem));
    }, [paragraph.data]);

    useSyncForm(syncFromProps);

    return (
        <div className="carousel-card-container">
            <SortableWrapper items={cards ?? []}
                             direction="horizontal"
                             className="carousel-card-list"
                             onDragSortEnd={update}
            >
                {cards?.map((card, index) => (
                    <SortableItemWrapper key={card.id}
                                        item={card}
                    >{dragHandle => (
                        <CarouselCard card={card.data}
                                      dragHandle={dragHandle}
                                      showDeleteButton={cards.length > 1}
                                      onChange={data => update(withArray(cards, index, { ...card, data }))}
                                      onDelete={() => update(toSpliced(cards, index, 1))}
                        />
                    )}</SortableItemWrapper>
                ))}
            </SortableWrapper>

            <Button variant="text-transparent"
                    className="size-6 p-1"
                    aria-label={intl.formatMessage({ id: "b0009" })}
                    onClick={() => update([...(cards ?? []), toSortableItem(getNewCarousel())])}
            >
                <AddIcon className="size-4" />
            </Button>
        </div>
    );
};

interface CarouselCardProps {
    showDeleteButton: boolean;
    dragHandle: React.ReactNode;
    card: CarouselCardItem;
    onChange: (item: CarouselCardItem) => void;
    onDelete: () => void;
}

const CarouselCard: React.FC<CarouselCardProps> = (props) => {
    const { card, onChange, onDelete, dragHandle, showDeleteButton } = props;

    const intl = useIntl();
    const { variableAutocomplete } = useScenarioAutocompletes();

    const isInvalidContent = !card.title && !card.text && !isValidMediaData(card);

    return (
        <div data-testid="carousel-card"
             className="carousel-card"
        >
            <div className="flex flex-col gap-2">
                <div className="carousel-card-header">
                    <div className="flex items-center gap-1">
                        {dragHandle}
                        <div className="field-label">
                            {intl.formatMessage({ id: "i04030" })}
                        </div>
                    </div>
                    {showDeleteButton && (
                        <Button aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={onDelete}
                        >
                            <DeleteIcon className="size-4 text-point-red" />
                        </Button>
                    )}
                </div>

                {isInvalidContent && (
                    <div className="text-point-red text-[13px] leading-none">
                        {intl.formatMessage({ id: "i04073" })}
                    </div>
                )}
                
                <div className="flex flex-col gap-2">
                    <MediaInput useImageSquare
                                data={card}
                                required={isInvalidContent}
                                onChange={value => onChange({ ...removeMediaData(card), ...value })}
                    />
                    {!!card.media_type && !!card.media_url && (
                        <MediaPreview mediaType={card.media_type}
                                      mediaUrl={card.media_url}
                                      width={(card.media_type === "audio" || card.media_type === "video") ? 300 : "100%"}
                                      height={card.media_type !== "video" ? 160 : "fit-content"}
                        />
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <TextInput required={isInvalidContent}
                               value={card.title ?? ""}
                               placeholder={intl.formatMessage({ id: "i04039" })}
                               aria-label={intl.formatMessage({ id: "i04039" })}
                               onChange={e => onChange({ ...card, title: e.target.value })}
                    />
                    <TextInput required={isInvalidContent}
                               autocompletes={variableAutocomplete}
                               defaultValue={card.text ?? ""}
                               placeholder={intl.formatMessage({ id: "i04040" })}
                               aria-label={intl.formatMessage({ id: "i04040" })}
                               onChange={e => onChange({ ...card, text: e.target.value })}
                    />
                </div>
            </div>

            <ButtonSortableList defaultButtons={card.buttons ?? []}
                                onChange={buttons => onChange({ ...card, buttons })}
            />
        </div>
    );
};

export default ParagraphOutputCarouselCardInputs;
